import { ReactNode } from 'react'
import { IconChevronUp, IconChevronDown } from '@tabler/icons-react'

export interface MyTableColumn<T> {
  key: string
  label: string
  sortKey?: string
  render?: (item: T) => ReactNode
  className?: string
  onCellClick?: (item: T) => void
}

interface MyTableProps<T> {
  columns: MyTableColumn<T>[]
  data: T[]
  currentSort?: string
  onSortChange?: (sortKey: string) => void
  isLoading?: boolean
  emptyText?: string
  actions?: (item: T) => ReactNode
}

export default function MyTable<T extends { id: string }>({
  columns,
  data,
  currentSort,
  onSortChange,
  isLoading,
  emptyText = 'Keine Einträge vorhanden',
  actions,
}: MyTableProps<T>) {

  function getSortIcon(sortKey?: string) {
    if (!sortKey || !currentSort) return null
    if (currentSort === sortKey) return <IconChevronUp size={14} />
    if (currentSort === `-${sortKey}`) return <IconChevronDown size={14} />
    return null
  }

  function handleSort(sortKey?: string) {
    if (!sortKey || !onSortChange) return
    if (currentSort === sortKey) {
      onSortChange(`-${sortKey}`)
    } else if (currentSort === `-${sortKey}`) {
      onSortChange('')
    } else {
      onSortChange(sortKey)
    }
  }

  return (
    <div className="overflow-x-auto">
      <table className="table w-full">
        <thead>
          <tr>
            {columns.map(col => (
              <th
                key={col.key}
                className={'text-xs uppercase tracking-wider opacity-60 font-semibold ' + (col.sortKey && onSortChange ? 'cursor-pointer select-none hover:opacity-100 transition-opacity ' : '') + (col.className || '')}
                onClick={() => handleSort(col.sortKey)}
              >
                <div className="flex items-center gap-1">
                  {col.label}
                  {col.sortKey && onSortChange && getSortIcon(col.sortKey)}
                </div>
              </th>
            ))}
            {actions && <th className="w-16"></th>}
          </tr>
        </thead>
        <tbody>
          {isLoading && (
            <tr>
              <td colSpan={columns.length + (actions ? 1 : 0)} className="text-center py-8">
                <span className="loading loading-spinner loading-md text-primary"></span>
              </td>
            </tr>
          )}
          {!isLoading && data.length === 0 && (
            <tr>
              <td colSpan={columns.length + (actions ? 1 : 0)} className="text-center py-8 opacity-60">
                {emptyText}
              </td>
            </tr>
          )}
          {!isLoading && data.map(item => (
            <tr key={item.id} className="hover:bg-secondary group">
              {columns.map(col => (
                <td
                  key={col.key}
                  className={(col.className || '') + (col.onCellClick ? ' cursor-pointer [&:hover_.title-hover-target]:underline' : '')}
                  onClick={col.onCellClick ? () => col.onCellClick!(item) : undefined}
                >
                  {col.render ? col.render(item) : (item as any)[col.key]}
                </td>
              ))}
              {actions && (
                <td className="text-right">
                  {actions(item)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
